/**
 * Satış Siparişleri Modülü — Sıfırdan Yeniden İnşa (spec msg-SS, 129 madde).
 *
 * İki ekran:
 *  1) SalesOrdersPage         — ana liste + filtreler + Yeni Sipariş modalı
 *  2) SalesOrderDetailPage    — ayrı route (/sales-orders/:id), 5 bölüm sıralaması:
 *       (1) Sipariş Bilgileri
 *       (2) Sipariş Kârlılık Analizi   ← yetki bekler (financial permission)
 *       (3) Üretim Planlama            ← Planlanan (ref) + Üretilecek (source-of-truth)
 *       (4) Envanter Kontrolü          ← canlı, SO onayından bağımsız
 *       (5) Formül Bilgileri           ← Formül · Ambalaj · Ek Parametreler · QC
 *
 * Historical veriler korunur; sadece UI/workflow yeniden düzenlenir.
 * Tüm operasyonel hesap kaynağı: line.production_quantity_kg  ("Üretilecek Miktar")
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, SO_STATUS_LABEL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Table, Btn, Modal, Input, Select, Badge, Card } from "@/components/UI";
import SmartSuggestInput from "@/components/SmartSuggestInput";
import LinkedFieldBanner from "@/components/LinkedFieldBanner";
import { useLinkedFields, detectConflicts } from "@/hooks/useLinkedFields";
import {
  Plus, CheckCircle, PackageOpen, ShoppingCart, Package, FileText, ArrowLeft,
  Search, Trash2, Pencil, DollarSign, Factory, Beaker, Wrench, ClipboardCheck,
  AlertTriangle, Link2,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useLiveEvents } from "@/lib/events";
import TelegramSendButton from "@/components/TelegramSendButton";
import MeetingsBanner from "@/components/MeetingsBanner";
import BulkInvWizard from "@/components/BulkInvWizard";
import { openPdfPreview } from "@/lib/pdfPreview";
import { usePagination, ListPagination } from "@/components/ListPagination";
import { todayTR } from "@/lib/date";

/* ============================================================================
 * Küçük yardımcılar
 * ==========================================================================*/
const PRIORITY_META = {
  low:    { label: "Düşük",  cls: "bg-yellow-100 text-yellow-800 border-yellow-300" },
  normal: { label: "Normal", cls: "bg-orange-100 text-orange-800 border-orange-300" },
  high:   { label: "Yüksek", cls: "bg-red-100 text-red-800 border-red-300" },
  urgent: { label: "Acil",   cls: "bg-red-600 text-white border-red-700" },
};
const PriorityBadge = ({ value }) => {
  const meta = PRIORITY_META[value] || PRIORITY_META.normal;
  return <span className={`inline-block text-[11px] font-semibold border rounded px-2 py-0.5 ${meta.cls}`} data-testid={`prio-${value}`}>{meta.label}</span>;
};
const fmt = (n, d = 2) => (Number.isFinite(n) ? Number(n).toFixed(d) : "—");
const today = () => todayTR();
// [msg-145 Bölüm 3+4] Kârlılık / iç maliyet / marj yalnızca Sistem Yöneticisi (admin) rolüne açıktır.
// Müşteri-facing Satış Fiyatı ayrı — Quotation PDF'te görünmeye devam eder.
const hasFinancialAccess = (user) => (user?.role || "") === "admin";

/* ============================================================================
 * ANA LİSTE — /sales-orders
 * ==========================================================================*/
export function SalesOrdersPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [formulas, setFormulas] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const emptyForm = { customer_id: "", customer_name: "", brand_id: "", project_no: "", formula_id: "", product_id: "", qty_ordered: 0, priority: "normal", required_delivery: "", customer_po: "", notes: "" };
  const [f, setF] = useState(emptyForm);
  const [projectSearch, setProjectSearch] = useState("");
  // Iter217 — Smart Linked Fields: provenance & conflict tracking
  const [provenance, setProvenance] = useState({});  // { field: "MP10482" } — set when auto-filled from a linked source
  const [dismissedConflicts, setDismissedConflicts] = useState({});  // { field: true } — user chose "keep mine"
  const linker = useLinkedFields();

  // Filters (spec msg-SS rule 91)
  const [filters, setFilters] = useState({
    order_no: "", customer_id: "", customer_code: "", project_no: "", product: "",
    priority: "", status: "", date_from: "", date_to: "",
  });
  const [sort, setSort] = useState("date_desc");

  const load = useCallback(async () => {
    const [s, c, b, p, fo] = await Promise.all([
      api.get("/sales-orders"),
      api.get("/customers"),
      api.get("/brands"),
      api.get("/products"),
      api.get("/formulas"),
    ]);
    setRows(s); setCustomers(c); setBrands(b); setProducts(p); setFormulas(fo);
  }, []);
  useEffect(() => { load(); }, [load]);
  useLiveEvents(["sales_order.approved", "sales_order.created", "sales_order.updated"], () => load());

  const cById = useMemo(() => Object.fromEntries(customers.map(c => [c.id, c])), [customers]);
  const bById = useMemo(() => Object.fromEntries(brands.map(b => [b.id, b])), [brands]);
  const pById = useMemo(() => Object.fromEntries(products.map(p => [p.id, p])), [products]);

  const filteredBrands = brands.filter(b => !f.customer_id || b.customer_id === f.customer_id);
  // iter217: Project selection is now handled by SmartSuggestInput (customer-context ranked).

  const pickProject = (fx) => {
    // spec msg-SS rules 7-9 — Project seçimi otomatik: customer/brand/product/formula/revision
    // Iter217: mark autofilled fields with provenance so UI can render badges &
    // detect user overrides later.
    const pno = fx.project_no || "";
    setF(prev => ({
      ...prev,
      project_no: pno,
      formula_id: fx.id,
      product_id: fx.product_id || prev.product_id,
      customer_id: fx.customer_id || prev.customer_id,
      customer_name: fx.customer_id
        ? (cById[fx.customer_id]?.legal_name || prev.customer_name || "")
        : prev.customer_name,
      brand_id: fx.brand_id || prev.brand_id,
    }));
    setProvenance({
      formula_id: pno || fx.id,
      product_id: fx.product_id ? (pno || fx.id) : null,
      customer_id: fx.customer_id ? (pno || fx.id) : null,
      brand_id: fx.brand_id ? (pno || fx.id) : null,
    });
    setDismissedConflicts({});
    setProjectSearch(`${pno || "?"} — ${fx.product_name || fx.name || "?"}${fx.revision ? " (" + fx.revision + ")" : ""}`);
  };

  // Iter217 — When a project/formula is bound, compare current form vs.
  // the authoritative linked-data and surface conflicts (customer/brand/product).
  const conflicts = useMemo(() => {
    if (!f.formula_id) return [];
    const fx = formulas.find(x => x.id === f.formula_id);
    if (!fx) return [];
    return detectConflicts(
      { customer_id: f.customer_id, brand_id: f.brand_id, product_id: f.product_id },
      { found: true, customer_id: fx.customer_id, brand_id: fx.brand_id, product_id: fx.product_id },
      ["customer_id", "brand_id", "product_id"],
    ).filter(c => !dismissedConflicts[c.field]);
  }, [f.formula_id, f.customer_id, f.brand_id, f.product_id, formulas, dismissedConflicts]);

  const applyExpected = (field) => {
    const fx = formulas.find(x => x.id === f.formula_id);
    if (!fx) return;
    const val = fx[field];
    if (field === "customer_id") {
      setF(prev => ({
        ...prev,
        customer_id: val,
        customer_name: cById[val]?.legal_name || prev.customer_name,
      }));
    } else {
      setF(prev => ({ ...prev, [field]: val }));
    }
  };

  const openNew = () => {
    setEditing(null);
    setF(emptyForm);
    setProjectSearch("");
    setProvenance({});
    setDismissedConflicts({});
    setOpen(true);
  };
  const openEdit = (r) => {
    const ln = (r.lines || [])[0] || {};
    setEditing(r);
    setF({
      customer_id: r.customer_id,
      customer_name: r.customer_name || (customers.find(c => c.id === r.customer_id)?.legal_name || ""),
      brand_id: r.brand_id,
      project_no: ln.project_no || "", formula_id: ln.formula_id || "",
      product_id: ln.product_id || "", qty_ordered: ln.qty_ordered || 0,
      priority: r.priority || "normal", required_delivery: (r.required_delivery || "").slice(0, 10),
      customer_po: r.customer_po || "", notes: r.notes || "",
    });
    const fx = formulas.find(x => x.id === ln.formula_id);
    setProjectSearch(fx ? `${fx.project_no || ""} — ${fx.product_name || fx.name || ""}${fx.revision ? " (" + fx.revision + ")" : ""}` : "");
    // Editing an existing SO — no fresh "provenance" (fields were persisted earlier);
    // conflict detection still runs against the formula's authoritative link so
    // historical mismatches surface but the user can dismiss them.
    setProvenance({});
    setDismissedConflicts({});
    setOpen(true);
  };
  const save = async () => {
    if (!f.customer_id && !(f.customer_name || "").trim()) return alert("Müşteri zorunlu");
    if (!f.brand_id) return alert("Marka zorunlu");
    if (!f.formula_id) return alert("Proje / Formül seçimi zorunlu");
    if (!Number.isFinite(+f.qty_ordered) || +f.qty_ordered <= 0) return alert("Sipariş Adeti pozitif tam sayı olmalı");
    const line = {
      product_id: f.product_id, formula_id: f.formula_id,
      project_no: f.project_no || null,
      qty_ordered: parseInt(f.qty_ordered, 10) || 0,
      unit: "adet",
    };
    const payload = {
      customer_id: f.customer_id || null,
      customer_name: f.customer_name || (customers.find(c => c.id === f.customer_id)?.legal_name || null),
      brand_id: f.brand_id,
      required_delivery: f.required_delivery || null,
      customer_po: f.customer_po || null, priority: f.priority || "normal",
      notes: f.notes || null,
      lines: [line],
    };
    try {
      if (editing) await api.put(`/sales-orders/${editing.id}`, payload);
      else await api.post("/sales-orders", payload);
      setOpen(false); setEditing(null); load();
    } catch (e) { alert(e.response?.data?.detail || "Kaydedilemedi"); }
  };
  const approve = async (r) => {
    if (!window.confirm(`${r.order_no} onaylansın ve otomatik üretim emri oluşturulsun mu?`)) return;
    try { await api.post(`/sales-orders/${r.id}/approve`); load(); }
    catch (e) { alert(e.response?.data?.detail || "Onaylanamadı"); }
  };
  const remove = async (r) => {
    if (!window.confirm(`${r.order_no} iptal edilsin/silinsin mi? (soft delete)`)) return;
    try { await api.del(`/sales-orders/${r.id}`); load(); }
    catch (e) { alert(e.response?.data?.detail || "Silinemedi"); }
  };

  // Filtered + sorted table rows
  const displayRows = useMemo(() => {
    let out = rows.slice();
    if (filters.order_no)     out = out.filter(r => (r.order_no || "").toLowerCase().includes(filters.order_no.toLowerCase()));
    if (filters.customer_id)  out = out.filter(r => r.customer_id === filters.customer_id);
    if (filters.customer_code)out = out.filter(r => (cById[r.customer_id]?.code || "").toLowerCase().includes(filters.customer_code.toLowerCase()));
    if (filters.project_no)   out = out.filter(r => (r.lines || []).some(ln => (ln.project_no || "").toLowerCase().includes(filters.project_no.toLowerCase())));
    if (filters.product)      out = out.filter(r => (r.lines || []).some(ln => (pById[ln.product_id]?.name || "").toLowerCase().includes(filters.product.toLowerCase())));
    if (filters.priority)     out = out.filter(r => (r.priority || "normal") === filters.priority);
    if (filters.status)       out = out.filter(r => r.status === filters.status);
    if (filters.date_from)    out = out.filter(r => (r.created_at || r.order_date || "") >= filters.date_from);
    if (filters.date_to)      out = out.filter(r => (r.created_at || r.order_date || "") <= filters.date_to + "T23:59:59Z");

    const cmp = {
      date_desc:  (a, b) => (b.created_at || b.order_date || "").localeCompare(a.created_at || a.order_date || ""),
      date_asc:   (a, b) => (a.created_at || a.order_date || "").localeCompare(b.created_at || b.order_date || ""),
      customer:   (a, b) => (cById[a.customer_id]?.legal_name || "").localeCompare(cById[b.customer_id]?.legal_name || ""),
      order_no:   (a, b) => (a.order_no || "").localeCompare(b.order_no || ""),
      priority:   (a, b) => ({urgent:0,high:1,normal:2,low:3})[a.priority||"normal"] - ({urgent:0,high:1,normal:2,low:3})[b.priority||"normal"],
    }[sort] || (() => 0);
    return out.sort(cmp);
  }, [rows, filters, sort, cById, pById]);

  const pg = usePagination(displayRows, "sales-orders", 100);

  return (
    <div>
      <PageHeader
        title="Satış Siparişleri"
        description="Müşteri sipariş yönetimi · Üretilecek Miktar canlı stok kontrolü"
        actions={<Btn onClick={openNew} data-testid="new-so"><Plus size={14}/> Yeni Sipariş</Btn>}
      />
      <MeetingsBanner department="SALES_ORDER" />

      {/* Filtreler + Sıralama */}
      <Card className="p-3 mb-4 bg-slate-50/70 border-slate-200">
        <div className="text-[11px] font-bold uppercase text-slate-500 mb-2">Filtreler</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <Input label="Sipariş No" value={filters.order_no} onChange={e => setFilters({ ...filters, order_no: e.target.value })} data-testid="flt-order-no" placeholder="SS10025"/>
          <Select label="Müşteri" value={filters.customer_id} onChange={e => setFilters({ ...filters, customer_id: e.target.value })} data-testid="flt-customer">
            <option value="">Tümü</option>{customers.map(c => <option key={c.id} value={c.id}>{c.legal_name}</option>)}
          </Select>
          <Input label="Müşteri No" value={filters.customer_code} onChange={e => setFilters({ ...filters, customer_code: e.target.value })} data-testid="flt-customer-code" placeholder="CUS-00007"/>
          <Input label="Proje No" value={filters.project_no} onChange={e => setFilters({ ...filters, project_no: e.target.value })} data-testid="flt-project" placeholder="MP10025"/>
          <Input label="Ürün" value={filters.product} onChange={e => setFilters({ ...filters, product: e.target.value })} data-testid="flt-product" placeholder="Hydra Glow"/>
          <Select label="Öncelik" value={filters.priority} onChange={e => setFilters({ ...filters, priority: e.target.value })} data-testid="flt-priority">
            <option value="">Tümü</option><option value="low">Düşük</option><option value="normal">Normal</option><option value="high">Yüksek</option><option value="urgent">Acil</option>
          </Select>
          <Select label="Durum" value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })} data-testid="flt-status">
            <option value="">Tümü</option>
            {Object.entries(SO_STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Select>
          <Input label="Tarih Başlangıç" type="date" value={filters.date_from} onChange={e => setFilters({ ...filters, date_from: e.target.value })} data-testid="flt-date-from"/>
          <Input label="Tarih Bitiş" type="date" value={filters.date_to} onChange={e => setFilters({ ...filters, date_to: e.target.value })} data-testid="flt-date-to"/>
          <Select label="Sıralama" value={sort} onChange={e => setSort(e.target.value)} data-testid="flt-sort">
            <option value="date_desc">En Yeni</option>
            <option value="date_asc">En Eski</option>
            <option value="customer">Müşteri</option>
            <option value="order_no">Sipariş Numarası</option>
            <option value="priority">Öncelik</option>
          </Select>
        </div>
      </Card>

      <Table testid="so-table" rows={pg.paged} onRowClick={r => nav(`/sales-orders/${r.id}`)} columns={[
        { label: "Sipariş No", render: r => <button onClick={(e) => { e.stopPropagation(); nav(`/sales-orders/${r.id}`); }} className="font-mono text-xs text-indigo-700 hover:underline font-semibold" data-testid={`open-so-${r.id}`}>{r.order_no}</button> },
        { label: "Müşteri No", render: r => <span className="font-mono text-xs">{cById[r.customer_id]?.code || "—"}</span> },
        { label: "Müşteri Adı", render: r => cById[r.customer_id]?.legal_name || "—" },
        { label: "Proje No", render: r => (r.lines || []).map(ln => ln.project_no).filter(Boolean).join(", ") || "—" },
        { label: "Ürün Adı", render: r => (r.lines || []).map(ln => pById[ln.product_id]?.name || "—").join(", ") },
        { label: "Sipariş Adeti", render: r => <span className="font-mono">{(r.lines || []).reduce((s, ln) => s + (ln.qty_ordered || 0), 0)}</span> },
        { label: "Sipariş Tarihi", render: r => (r.created_at || r.order_date || "").slice(0, 10) },
        { label: "Öncelik", render: r => <PriorityBadge value={r.priority || "normal"}/> },
        { label: "Durum", render: r => <Badge level={r.status === "completed" || r.status === "delivered" || r.status === "shipped" ? "ok" : r.status === "cancelled" ? "inactive" : r.status === "delayed" ? "missing" : "attention"}>{SO_STATUS_LABEL[r.status] || r.status}</Badge> },
        { label: "İşlem", render: r => (
          <div className="flex gap-1" onClick={e => e.stopPropagation()}>
            <Btn size="sm" variant="secondary" onClick={() => nav(`/sales-orders/${r.id}`)} data-testid={`view-so-${r.id}`}>Detay</Btn>
            {r.status === "draft" && <Btn size="sm" onClick={() => approve(r)} data-testid={`approve-so-list-${r.id}`}><CheckCircle size={12}/> Onayla</Btn>}
            {r.status === "draft" && <Btn size="sm" variant="secondary" onClick={() => openEdit(r)} data-testid={`edit-so-${r.id}`}><Pencil size={12}/> Düzenle</Btn>}
            {["draft", "awaiting_approval", "cancelled", "approved"].includes(r.status) && r.status !== "shipped" && r.status !== "delivered" && <Btn size="sm" variant="danger" onClick={() => remove(r)} data-testid={`delete-so-${r.id}`}><Trash2 size={12}/></Btn>}
          </div>
        ) },
      ]}/>
      <ListPagination pagination={pg} testid="so-pagination"/>
      {displayRows.length === 0 && <div className="text-center py-8 text-slate-400 text-sm">Filtreye uygun sipariş yok.</div>}

      {/* YENİ SİPARİŞ MODALI */}
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? `Siparişi Düzenle — ${editing.order_no}` : "Yeni Satış Siparişi"} size="lg">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="col-span-1 sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <SmartSuggestInput
              label="Müşteri *"
              value={f.customer_name || (cById[f.customer_id]?.legal_name ?? "")}
              selectedId={f.customer_id}
              items={customers}
              displayField="legal_name"
              idField="id"
              secondaryField={(c) => [c.code && `Kod: ${c.code}`, c.tax_id && `VKN: ${c.tax_id}`, c.city].filter(Boolean).join(" · ")}
              placeholder="Müşteri adı yazın veya listeden seçin..."
              helperText={
                provenance.customer_id
                  ? (<><Link2 size={10}/> {provenance.customer_id} projesinden getirildi</>)
                  : null
              }
              onChange={({ value, selectedId }) => {
                setF({
                  ...f,
                  customer_name: value,
                  customer_id: selectedId || "",
                  brand_id: selectedId === f.customer_id ? f.brand_id : "",
                });
                // User is now editing the customer → provenance for that field
                // no longer applies (they own the value).
                if (provenance.customer_id) {
                  setProvenance(p => ({ ...p, customer_id: null }));
                }
              }}
              data-testid="so-customer"
            />
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Müşteri Numarası (otomatik)</label>
              <div className="px-3 py-2 rounded bg-slate-100 border border-slate-200 font-mono text-sm" data-testid="so-customer-code">
                <input readOnly value={cById[f.customer_id]?.code || ""} placeholder="—" className="w-full bg-transparent outline-none font-mono text-sm" data-testid="so-customer-code-input"/>
              </div>
            </div>
          </div>

          <Select label="Marka *" value={f.brand_id} onChange={e => { setF({ ...f, brand_id: e.target.value }); if (provenance.brand_id) setProvenance(p => ({ ...p, brand_id: null })); }} data-testid="so-brand">
            <option value="">Seçiniz...</option>
            {filteredBrands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Select>

          {/* Iter217 — Smart Linked Project Field: SmartSuggestInput with
              customer-context prioritization + conflict warning below. */}
          <div>
            <SmartSuggestInput
              label="Proje Numarası *"
              value={projectSearch}
              selectedId={f.formula_id}
              items={formulas}
              displayField="project_no"
              idField="id"
              secondaryField={(fx) => `${fx.product_name || fx.name || "?"} · ${cById[fx.customer_id]?.legal_name || "?"} · Rev: ${fx.revision || "—"}`}
              searchAccessor={(fx) => [fx.product_name, fx.name]}
              placeholder="MP10125 veya ürün adı yazın..."
              priorityFilter={f.customer_id ? (fx) => fx.customer_id === f.customer_id : null}
              priorityLabel="Bu müşterinin"
              helperText={
                f.formula_id ? (
                  <><CheckCircle size={10}/> Formül {String(f.formula_id).slice(0, 8)}… · Ürün {pById[f.product_id]?.name || "—"} · Rev {formulas.find(x => x.id === f.formula_id)?.revision || "—"}</>
                ) : null
              }
              onChange={({ value, selectedId, item }) => {
                if (item) {
                  pickProject(item);
                } else {
                  setProjectSearch(value);
                  if (!value) {
                    setF({ ...f, formula_id: "", project_no: "", product_id: "" });
                    setProvenance({});
                    setDismissedConflicts({});
                  }
                }
              }}
              data-testid="so-project"
            />
          </div>

          {/* Iter217 — Conflict banner (non-blocking) */}
          <div className="col-span-1 sm:col-span-2">
            <LinkedFieldBanner
              conflicts={conflicts}
              labels={{
                customer_id: "Müşteri",
                brand_id: "Marka",
                product_id: "Ürün",
              }}
              resolve={{
                current: (field, val) => {
                  if (field === "customer_id") return cById[val]?.legal_name || val || "—";
                  if (field === "brand_id") return brands.find(b => b.id === val)?.name || val || "—";
                  if (field === "product_id") return pById[val]?.name || val || "—";
                  return val;
                },
                expected: (field, val) => {
                  if (field === "customer_id") return cById[val]?.legal_name || val || "—";
                  if (field === "brand_id") return brands.find(b => b.id === val)?.name || val || "—";
                  if (field === "product_id") return pById[val]?.name || val || "—";
                  return val;
                },
              }}
              onApplyExpected={applyExpected}
              onKeepCurrent={(field) => setDismissedConflicts(prev => ({ ...prev, [field]: true }))}
            />
          </div>

          {/* Auto-linked (read-only summary) */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Ürün Adı (otomatik)</label>
            <div className="px-3 py-2 rounded bg-slate-100 border border-slate-200 text-sm" data-testid="so-product-name">
              {pById[f.product_id]?.name || "—"}
              {provenance.product_id && (
                <span className="ml-2 text-[10px] text-emerald-700"><Link2 size={9} className="inline"/> {provenance.product_id} projesinden</span>
              )}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Aktif Formül Revizyonu (otomatik)</label>
            <div className="px-3 py-2 rounded bg-slate-100 border border-slate-200 font-mono text-sm" data-testid="so-formula-rev">
              {formulas.find(x => x.id === f.formula_id)?.revision || "—"}
            </div>
          </div>

          <Input label="Sipariş Adeti *" type="number" min="1" step="1" value={f.qty_ordered} onChange={e => setF({ ...f, qty_ordered: e.target.value })} data-testid="so-qty-ordered"/>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Birim Satış Fiyatı (otomatik — formülden)</label>
            <div className="px-3 py-2 rounded bg-emerald-50 border border-emerald-200 font-mono text-sm font-semibold text-emerald-800" data-testid="so-formula-sales-price">
              {(() => {
                const fx = formulas.find(x => x.id === f.formula_id);
                if (!fx) return <span className="text-slate-400 italic font-normal">Formül seçin</span>;
                const price = Number(fx.sales_price_eur || 0);
                return price > 0 ? `€ ${price.toFixed(4)}` : <span className="text-slate-400 italic font-normal">Formülde tanımlı değil</span>;
              })()}
            </div>
          </div>
          <Select label="Öncelik" value={f.priority} onChange={e => setF({ ...f, priority: e.target.value })} data-testid="so-priority">
            <option value="low">Düşük</option>
            <option value="normal">Normal</option>
            <option value="high">Yüksek</option>
            <option value="urgent">Acil</option>
          </Select>
          <Input label="Teslim Tarihi" type="date" value={f.required_delivery} onChange={e => setF({ ...f, required_delivery: e.target.value })} data-testid="so-delivery-date"/>
          <Input label="Müşteri PO" value={f.customer_po} onChange={e => setF({ ...f, customer_po: e.target.value })} data-testid="so-customer-po"/>
          <div className="col-span-2">
            <label className="block text-xs font-semibold text-slate-500 mb-1">Notlar</label>
            <textarea rows={2} value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} className="w-full px-3 py-2 text-xs border border-slate-300 rounded" data-testid="so-notes"/>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Btn variant="secondary" onClick={() => setOpen(false)}>Vazgeç</Btn>
          <Btn onClick={save} data-testid="save-so"><CheckCircle size={14}/> Kaydet</Btn>
        </div>
      </Modal>
    </div>
  );
}

/* ============================================================================
 * DETAY SAYFASI — /sales-orders/:id
 * ==========================================================================*/
export function SalesOrderDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [brand, setBrand] = useState(null);
  const [products, setProducts] = useState([]);
  const [formulas, setFormulas] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [plannedRef, setPlannedRef] = useState(null);   // reference-only planned kg
  const [prodKgDraft, setProdKgDraft] = useState({});   // per-line-index string
  const [invDraft, setInvDraft] = useState(null);       // manual→inventory modal
  const [invBusyKey, setInvBusyKey] = useState(null);   // Fix Pkg 04 Bug A: row-scoped loading key
  const [poDraft, setPoDraft] = useState(null);         // purchase modal
  const [resPopover, setResPopover] = useState(null);   // Fix Pkg 03: rezervasyon riski popover
  // Iter 92: Toplu Envanter Kaydet wizard (Formuller ile aynı komponent)
  const [bulkInv, setBulkInv] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    const [o, p, fo, m, sp, ref] = await Promise.all([
      api.get(`/sales-orders/${id}`),
      api.get("/products"),
      api.get("/formulas"),
      api.get("/materials"),
      api.get("/suppliers"),
      api.get(`/sales-orders/${id}/planned-production-kg`).catch(() => null),
    ]);
    setOrder(o); setProducts(p); setMaterials(m); setSuppliers(sp);
    // Ensure the SO's own formula is available even if not in the active-only list.
    // (Fixes iter51 HIGH bug: sub-formula/sub-packaging/sub-extras/sub-qc missing for
    //  SOs referencing revised/superseded formulas.)
    const foMerged = Array.isArray(fo) ? [...fo] : [];
    const soFormulaId = (o?.lines || [])[0]?.formula_id;
    if (soFormulaId && !foMerged.some(x => x.id === soFormulaId)) {
      try {
        const specific = await api.get(`/formulas/${soFormulaId}`);
        if (specific?.id) foMerged.push(specific);
      } catch (_) { /* ignore — will fall through to 'Formül bulunamadı' */ }
    }
    setFormulas(foMerged);
    setPlannedRef(ref);
    // Load customer + brand
    if (o?.customer_id) { try { const c = await api.get(`/customers`); setCustomer((c || []).find(x => x.id === o.customer_id) || null); } catch (_) {} }
    if (o?.brand_id)    { try { const b = await api.get(`/brands`);    setBrand((b || []).find(x => x.id === o.brand_id) || null); } catch (_) {} }
    // Sync draft input from server state — but preserve any value the user has already
    // typed so background 3s polling does NOT overwrite in-flight edits (iter69 fix).
    setProdKgDraft(prev => {
      const next = { ...prev };
      (o?.lines || []).forEach((ln, i) => {
        const cur = next[i];
        if (cur === undefined || cur === "") {
          if (ln.production_quantity_kg != null || ln.production_bulk_kg != null) {
            next[i] = String(ln.production_quantity_kg ?? ln.production_bulk_kg ?? "");
          }
        }
        // Else: preserve user's in-progress input (they will click "Uygula" to persist).
      });
      return next;
    });
  }, [id]);
  useEffect(() => { load(); }, [load]);
  // Live push (spec msg-SS rule 89)
  useLiveEvents(
    ["inventory.lot.created", "inventory.lot.approved", "inventory.lot.adjusted", "inventory.consumed",
     "purchase_order.created", "purchase_order.updated", "purchase_order.approved",
     "purchase_order.cancelled", "sales_order.approved"],
    () => load()
  );
  // Canlı envanter takibi — sayfa açıkken 3 saniyede bir stok verilerini
  // yenile ki başka üretimlerden gelen rezervasyon/onay değişiklikleri neredeyse
  // anlık görünsün. Ayrıca pencere focus'a girince ve sekme visible olunca da
  // anında yenile (SWR-benzeri revalidate on focus / on mount).
  useEffect(() => {
    const t = setInterval(() => {
      // Fix Pkg 04 follow-up: envanter kaydı sırasında polling'i skip et — race yok
      if (invBusyKey) return;
      load();
    }, 3000);
    const onFocus = () => { if (!invBusyKey) load(); };
    const onVisible = () => { if (document.visibilityState === "visible" && !invBusyKey) load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load, invBusyKey]);

  const mById = useMemo(() => Object.fromEntries(materials.map(m => [m.id, m])), [materials]);

  if (!order) return <div className="p-6 text-slate-500">Yükleniyor...</div>;

  const line = order.lines?.[0] || {}; // Kural: 1 SO = 1 kalem (spec akış, tek proje/formül)
  const product = products.find(p => p.id === line.product_id) || {};
  const formula = formulas.find(f => f.id === line.formula_id) || {};

  const qtyOrdered = line.qty_ordered || 0;
  const prodKg = parseFloat(prodKgDraft[0] || line.production_quantity_kg || line.production_bulk_kg || 0) || 0;
  const plannedKg = plannedRef?.lines?.[0]?.planned_production_kg;

  // ----------- Kârlılık (permission gated) -----------
  const showFinancial = hasFinancialAccess(user);
  const unitCost = (line.unit_cost_snapshot != null)
    ? line.unit_cost_snapshot
    : (() => {
        // Live compute from formula master when no snapshot yet
        if (!formula || !qtyOrdered) return null;
        let raw = 0;
        const fillQty = product.fill_qty || 0;
        const density = formula.density || 1;
        const theoretical = (qtyOrdered * fillQty * density) / 1000;
        const loss = ((formula.process_loss_pct || 0) + (formula.fill_loss_pct || 0)) / 100;
        const gross = loss < 0.99 ? theoretical / (1 - loss) : theoretical;
        (formula.lines || []).forEach(fl => {
          const need = gross * (fl.percentage || 0) / 100;
          const mat = mById[fl.material_id];
          if (mat) raw += need * (mat.unit_cost || 0);
        });
        let pkg = 0;
        (formula.packaging_lines || []).forEach(pl => {
          const mat = mById[pl.material_id];
          if (mat) pkg += qtyOrdered * (pl.qty_per_unit || 1) * (mat.unit_cost || 0);
        });
        const extras = (formula.extra_costs || []).reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
        const total = raw + pkg + extras * qtyOrdered;
        return qtyOrdered > 0 ? total / qtyOrdered : 0;
      })();
  const unitPrice = line.unit_sales_price_snapshot != null ? line.unit_sales_price_snapshot : (line.unit_price || formula.sales_price_eur || 0);
  const unitProfit = unitCost != null ? unitPrice - unitCost : null;
  const totalCost = unitCost != null ? unitCost * qtyOrdered : null;
  const totalRev  = unitPrice * qtyOrdered;
  const totalProfit = unitProfit != null ? unitProfit * qtyOrdered : null;
  const margin = totalRev > 0 && totalProfit != null ? (totalProfit / totalRev * 100) : null;
  const currency = line.sales_price_currency_snapshot || order.currency || "EUR";

  // ----------- Üretilecek Miktar apply -----------
  const applyProdKg = async (idx) => {
    const val = parseFloat(prodKgDraft[idx]);
    if (!Number.isFinite(val) || val < 0) return alert("Geçerli bir kg değeri girin");
    try {
      await api.get(`/sales-orders/${id}`);  // no-op, keep session warm
      await fetchPatchProdKg(idx, val);
      load();
    } catch (e) { alert(e.response?.data?.detail || "Kaydedilemedi"); }
  };
  const fetchPatchProdKg = async (idx, val) => {
    const token = localStorage.getItem("erp_token");
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sales-orders/${id}/lines/${idx}/production-quantity`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ production_quantity_kg: val }),
    });
    if (!r.ok) { const j = await r.json().catch(() => ({})); throw { response: { data: j } }; }
  };

  // ----------- Approval -----------
  const approve = async () => {
    if (!window.confirm(`${order.order_no} onaylansın ve otomatik üretim emri oluşturulsun mu?`)) return;
    try {
      await api.post(`/sales-orders/${id}/approve`);
      load();
    } catch (e) { alert(e.response?.data?.detail || "Onaylanamadı"); }
  };

  // ----------- Manuel malzeme → envanter kaydı -----------
  // Fix Pkg 04 Bug A: satır-bazlı loading key, duplicate recovery, hard link + reconcile.
  // Follow-up (buton hala takılıyor): window.confirm KALDIRILDI (blocking, race prone),
  // optimistic satır güncellemesi + double-load retry eklendi.
  const saveInv = async () => {
    if (!invDraft?.material_name?.trim()) return alert("Malzeme adı zorunlu");
    if (!invDraft?.category) return alert("Kategori zorunlu");
    if (invBusyKey) return; // önceki işlem devam ediyor
    const name = invDraft.material_name.trim();
    const nameLower = name.toLowerCase();
    setInvBusyKey(name);
    let matId = null;
    let matName = name;
    let matCode = "";
    try {
      try {
        const mat = await api.post("/materials", {
          name, category: invDraft.category, unit: invDraft.unit || "kg",
          unit_cost: parseFloat(invDraft.unit_cost || 0), primary_supplier: invDraft.primary_supplier || "",
          currency: invDraft.currency || "EUR",
          min_stock: parseFloat(invDraft.min_stock || 0), critical_stock: parseFloat(invDraft.critical_stock || 0),
          lead_time_days: parseInt(invDraft.lead_time_days || 0),
        });
        matId = mat.id; matName = mat.name; matCode = mat.code || "";
      } catch (e) {
        // Recovery (Bug A madde 7-8): mevcut kayıt varsa yeni oluşturma, mevcut ile eşleştir
        const err = e.response?.data;
        if (err?.detail?.code === "duplicate_material_name" && err.detail.existing_id) {
          matId = err.detail.existing_id;
          matCode = err.detail.existing_code || "";
          matName = name;
        } else {
          throw e;
        }
      }
      // Hard-link (formula lines + reservations + inventory_check recompute)
      const linkResp = await api.post("/formulas/link-manual-material", { material_id: matId, material_name: name });
      // Fix Pkg 05 follow-up: match başarısızsa optimistic UI'yi UYGULAMA ve kullanıcıya
      // net uyarı ver. Aksi halde 3s polling backend truth'una göre "needs_inventory"
      // dönecek ve buton tekrar açılacak.
      if (!linkResp?.formulas_updated) {
        setInvDraft(null);
        alert(`⚠️ Malzeme kaydedildi (${matName}) ama formüldeki manuel malzeme ile eşleşemedi.\n\nMuhtemel neden: formüldeki malzeme adı farklı yazılmış. Formülü açıp o satırdaki adı buradaki isimle aynı yapın veya elle "Malzeme Seç" ile bu envanter kaydını bağlayın.\n\nGirilen ad: "${name}"`);
        return;
      }
      // Iter96 (Fix Pkg 02): Bug A — 5. mapping takılma root cause: frontend
      // row-eşleşme casefold != backend casefold. Backend'in `_norm` fonksiyonu
      // `re.sub(r"\s+", " ").casefold()` yapar. Frontend'de eşdeğeri:
      //   .trim() + collapse whitespace + toLocaleLowerCase("tr")
      const norm = (v) => (v || "").trim().replace(/\s+/g, " ").toLocaleLowerCase("tr");
      const nameNorm = norm(name);
      setOrder(prev => {
        if (!prev?.inventory_check?.items) return prev;
        const items = prev.inventory_check.items.map(it => {
          const rowName = norm(it.material_name || it.manual_material_name);
          if (it.status === "needs_inventory" && rowName === nameNorm) {
            return { ...it, material_id: matId, needs_inventory: false, status: "shortage",
                     shortage: it.required || 0, manual_material_name: null };
          }
          return it;
        });
        return { ...prev, inventory_check: { ...prev.inventory_check, items } };
      });
      // Modal'ı kapat
      setInvDraft(null);
      // Retry-safe reload: primary + delayed follow-up (MongoDB propagation buffer).
      try { await load(); } catch (_) {}
      setTimeout(() => { try { load(); } catch (_) {} }, 700);
      // Iter96: safety net — even after 1.5s if backend hasn't converged,
      // one more reload guarantees the "needs_inventory" chip clears.
      setTimeout(() => { try { load(); } catch (_) {} }, 1500);
      // Bilgilendirme (blocking confirm YOK; toast benzeri kısa mesaj + async PO seçimi)
      const parts = [`✅ Envanter kaydı bağlandı: ${matName}${matCode ? ` (${matCode})` : ""}`];
      if (linkResp?.formulas_updated) parts.push(`${linkResp.formulas_updated} formül eşleşti`);
      if (linkResp?.reservations_reconciled) parts.push(`${linkResp.reservations_reconciled} sipariş rezervasyonu güncellendi`);
      // Non-blocking: setTimeout ile confirm göster → state clear'ı tamamlansın diye
      setTimeout(() => {
        const openPo = window.confirm(`${parts.join(" · ")}\n\nHemen Satın Alma taslağı açmak ister misiniz?`);
        if (openPo) setPoDraft({ material_id: matId, material_name: matName, unit: invDraft?.unit || "kg", supplier: "", qty: invDraft?.suggested_qty || 0, unit_price: parseFloat(invDraft?.unit_cost || 0), expected_delivery: "" });
      }, 50);
    } catch (e) {
      const err = e.response?.data;
      alert(err?.detail?.message || err?.detail || "Envanter kaydı oluşturulamadı");
    } finally {
      setInvBusyKey(null);
    }
  };

  // Iter 92: Toplu Envanter Kaydet (SO detay) — hepsini tek modalda topla + kaydet
  const openBulkInvSo = () => {
    const items = order?.inventory_check?.items || [];
    const rows = items
      .filter(it => it.status === "needs_inventory")
      .map(it => ({
        kind: it.kind === "packaging" ? "pkg" : "raw",
        index: 0, // SO tarafında line_index yerine material_name ile eşleşme
        name: it.manual_material_name || it.material_name || "",
        unit: it.unit || (it.kind === "packaging" ? "adet" : "kg"),
        unit_cost: 0,
        category: it.kind === "packaging" ? "other_packaging" : "raw_material",
        status: "pending",
        required: it.required,
      }));
    if (rows.length === 0) { alert("Envantere kaydedilecek manuel malzeme yok"); return; }
    setBulkInv({ rows });
  };

  const saveBulkInvSo = async () => {
    if (bulkBusy || !bulkInv) return;
    for (const r of bulkInv.rows) {
      if (!r.name?.trim()) { alert(`Boş isim: ${r.kind} #${r.index + 1}`); return; }
      if (!r.category) { alert(`Kategori seçilmedi: ${r.name}`); return; }
    }
    setBulkBusy(true);
    const successNames = new Set();
    for (let i = 0; i < bulkInv.rows.length; i++) {
      const r = bulkInv.rows[i];
      if (r.status === "success") continue;
      try {
        let matId = null;
        try {
          const mat = await api.post("/materials", {
            name: r.name.trim(), category: r.category, unit: r.unit || "kg",
            unit_cost: parseFloat(r.unit_cost || 0), primary_supplier: "", currency: "EUR",
            min_stock: 0, critical_stock: 0, lead_time_days: 0,
          });
          matId = mat.id;
        } catch (e) {
          const err = e.response?.data;
          if (err?.detail?.code === "duplicate_material_name" && err.detail.existing_id) matId = err.detail.existing_id;
          else throw e;
        }
        const link = await api.post("/formulas/link-manual-material", { material_id: matId, material_name: r.name.trim() });
        const linked = !!link?.formulas_updated;
        setBulkInv(prev => prev ? { rows: prev.rows.map((x, j) => j === i ? { ...x, status: linked ? "success" : "warn_no_match", mat_id: matId } : x) } : prev);
        if (linked) successNames.add(r.name.trim().toLowerCase());
      } catch (e) {
        const err = e.response?.data;
        setBulkInv(prev => prev ? { rows: prev.rows.map((x, j) => j === i ? { ...x, status: "error", error: err?.detail?.message || err?.detail || "Hata" } : x) } : prev);
      }
    }
    // Optimistic: SO inventory_check items içinde matched name'leri "shortage" yap
    // Iter96: whitespace-collapse + Türkçe casefold — backend `_norm` ile senkron.
    const norm = (v) => (v || "").trim().replace(/\s+/g, " ").toLocaleLowerCase("tr");
    if (successNames.size > 0) {
      const normSet = new Set([...successNames].map(norm));
      setOrder(prev => {
        if (!prev?.inventory_check?.items) return prev;
        const items = prev.inventory_check.items.map(it => {
          const nm = norm(it.material_name || it.manual_material_name);
          if (it.status === "needs_inventory" && normSet.has(nm)) {
            return { ...it, needs_inventory: false, status: "shortage", shortage: it.required || 0, manual_material_name: null };
          }
          return it;
        });
        return { ...prev, inventory_check: { ...prev.inventory_check, items } };
      });
    }
    setBulkBusy(false);
    // Reload SO for backend truth (link endpoint reconciles reservations + persists inventory_check)
    try { await load(); } catch (_) {}
    setTimeout(() => { try { load(); } catch (_) {} }, 700);
    // Iter96: extra safety net for stubborn 5th-row race.
    setTimeout(() => { try { load(); } catch (_) {} }, 1500);
  };
  const savePo = async () => {
    if (!poDraft?.supplier?.trim()) return alert("Tedarikçi zorunlu");
    if (!poDraft?.qty || parseFloat(poDraft.qty) <= 0) return alert("Miktar > 0 olmalı");
    try {
      const r = await api.post("/purchase-orders/from-shortage", {
        material_id: poDraft.material_id, qty: parseFloat(poDraft.qty), unit: poDraft.unit,
        unit_price: parseFloat(poDraft.unit_price || 0), supplier: poDraft.supplier.trim(),
        expected_delivery: poDraft.expected_delivery || undefined, linked_sales_order_id: id,
      });
      setPoDraft(null); alert(r.consolidated ? `Mevcut taslağa eklendi: ${r.po_no}` : `Yeni taslak: ${r.po_no}`); load();
    } catch (e) { alert(e.response?.data?.detail || "PO oluşturulamadı"); }
  };

  // ----------- Envanter Kontrolü satırı -----------
  const invItems = order.inventory_check?.items || [];
  const rawItems = invItems.filter(i => i.kind === "raw");
  const pkgItems = invItems.filter(i => i.kind === "packaging");

  const downloadPdf = () => {
    const token = localStorage.getItem("erp_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sales-orders/${id}/detail-pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob(); })
      .then(b => openPdfPreview(b, `${order.order_no || "SS"}.pdf`))
      .catch(e => alert(`PDF alınamadı: ${e.message}`));
  };

  return (
    <div>
      <PageHeader
        title={`Satış Siparişi ${order.order_no}`}
        description={<div className="flex items-center gap-2"><PriorityBadge value={order.priority || "normal"}/> <Badge level={order.status === "completed" || order.status === "delivered" ? "ok" : order.status === "cancelled" ? "inactive" : "attention"}>{SO_STATUS_LABEL[order.status] || order.status}</Badge></div>}
        actions={
          <>
            <Btn variant="secondary" onClick={() => nav("/sales-orders")} data-testid="back-to-list"><ArrowLeft size={14}/> Listeye</Btn>
            <Btn variant="secondary" onClick={downloadPdf} data-testid="so-pdf" title="Sipariş PDF İndir"><FileText size={14}/></Btn>
            {order.customer_id && (
              <TelegramSendButton customer_id={order.customer_id} kind="order_status" entity_ref={order.order_no}
                label="Sipariş Durumu"
                defaultMessage={`📋 Sipariş Bilgilendirmesi\n\nSipariş No: ${order.order_no}\nDurum: ${SO_STATUS_LABEL[order.status] || order.status}\nTeslim Tarihi: ${(order.required_delivery || "—").slice(0, 10)}\n\n— Meta Pharma`}
                testid="tg-so-status"/>
            )}
            {order.status === "draft" && <Btn onClick={approve} data-testid="approve-so"><CheckCircle size={14}/> Onayla</Btn>}
          </>
        }
      />

      {/* ============= 1) SİPARİŞ BİLGİLERİ ============= */}
      <Card className="p-4 mb-4" data-testid="section-order-info">
        <div className="text-sm font-bold mb-3 flex items-center gap-2 text-indigo-800"><ClipboardCheck size={14}/> 1. Sipariş Bilgileri</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Info label="Sipariş Numarası" value={<span className="font-mono font-semibold">{order.order_no}</span>}/>
          <Info label="Müşteri Numarası" value={<span className="font-mono">{customer?.code || "—"}</span>}/>
          <Info label="Müşteri Adı" value={customer?.legal_name || "—"}/>
          <Info label="Marka" value={brand?.name || "—"}/>
          <Info label="Proje Numarası" value={<span className="font-mono">{line.project_no || "—"}</span>}/>
          <Info label="Formül Revizyonu" value={<span className="font-mono">{line.formula_revision || formula.revision || "—"}</span>}/>
          <Info label="Ürün Adı" value={product.name || "—"}/>
          <Info label="Sipariş Adeti" value={<span className="font-mono font-semibold">{qtyOrdered}</span>}/>
          <Info label="Birim Satış Fiyatı" value={<span className="font-mono font-semibold text-emerald-700" data-testid="so-detail-unit-price">€ {fmt(Number(line.unit_sales_price_snapshot ?? line.unit_price ?? formula.sales_price_eur ?? 0), 4)}</span>}/>
          <Info label="Sipariş Tarihi" value={(order.created_at || order.order_date || "").slice(0, 10)}/>
          <Info label="Teslim Tarihi" value={(order.required_delivery || "—").slice(0, 10)}/>
          <Info label="Öncelik" value={<PriorityBadge value={order.priority || "normal"}/>}/>
          <Info label="Durum" value={<Badge level={order.status === "completed" || order.status === "delivered" ? "ok" : order.status === "cancelled" ? "inactive" : "attention"}>{SO_STATUS_LABEL[order.status] || order.status}</Badge>}/>
        </div>
      </Card>

      {/* ============= 2) SİPARİŞ KÂRLILIK ANALİZİ ============= */}
      {showFinancial && (
        <Card className="p-4 mb-4 bg-gradient-to-r from-emerald-50/70 to-teal-50/40 border-emerald-200" data-testid="section-profit">
          <div className="text-sm font-bold mb-3 flex items-center gap-2 text-emerald-800"><DollarSign size={14}/> 2. Sipariş Kârlılık Analizi</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Info label="Birim Maliyet" value={<span className="font-mono">{unitCost != null ? `${fmt(unitCost, 4)} ${currency}` : "—"}</span>} testid="pf-unit-cost"/>
            <Info label="Birim Satış" value={<span className="font-mono">{fmt(unitPrice, 4)} {currency}</span>} testid="pf-unit-price"/>
            <Info label="Birim Kâr" value={<span className={`font-mono font-semibold ${unitProfit != null && unitProfit >= 0 ? "text-emerald-700" : "text-red-600"}`}>{unitProfit != null ? `${fmt(unitProfit, 4)} ${currency}` : "—"}</span>} testid="pf-unit-profit"/>
            <Info label="Toplam Maliyet" value={<span className="font-mono">{totalCost != null ? `${fmt(totalCost, 2)} ${currency}` : "—"}</span>} testid="pf-total-cost"/>
            <Info label="Toplam Satış" value={<span className="font-mono">{fmt(totalRev, 2)} {currency}</span>} testid="pf-total-rev"/>
            <Info label="Toplam Kâr" value={<span className={`font-mono font-bold ${totalProfit != null && totalProfit >= 0 ? "text-emerald-700" : "text-red-600"}`}>{totalProfit != null ? `${fmt(totalProfit, 2)} ${currency}` : "—"}</span>} testid="pf-total-profit"/>
            <Info label="Kâr Marjı" value={<span className={`font-mono font-bold ${margin != null && margin >= 0 ? "text-emerald-700" : "text-red-600"}`}>{margin != null ? `%${fmt(margin, 1)}` : "—"}</span>} testid="pf-margin"/>
            <Info label="Snapshot" value={line.snapshot_at ? <span className="text-[10px] text-emerald-700">✓ {(line.snapshot_at).slice(0, 10)}</span> : <span className="text-[10px] text-slate-400 italic">canlı hesap</span>}/>
          </div>
        </Card>
      )}

      {/* ============= 3) ÜRETİM PLANLAMA ============= */}
      <Card className="p-4 mb-4 border-l-4 border-indigo-500 bg-indigo-50/30" data-testid="section-production">
        <div className="text-sm font-bold mb-3 flex items-center gap-2 text-indigo-800"><Factory size={14}/> 3. Üretim Planlama</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Planlanan Üretim Miktarı (REFERANS ONLY - spec rule 27-28) */}
          <div className="bg-white border border-slate-200 rounded p-3">
            <div className="text-xs font-semibold text-slate-500 mb-1 flex items-center gap-1">Planlanan Üretim Miktarı <span className="text-[9px] font-normal italic text-amber-600">(yalnızca referans — operasyonel değildir)</span></div>
            <div className="font-mono text-2xl font-bold text-slate-700" data-testid="planned-kg">
              {plannedKg != null ? <>{plannedKg.toFixed(3)} <span className="text-sm font-normal text-slate-400">kg</span></> : <span className="text-sm italic text-slate-400">— (master data eksik)</span>}
            </div>
            <div className="text-[10px] text-slate-500 mt-1">
              Hesap: {plannedRef?.lines?.[0]?.detail ? (
                <>Sipariş Adeti × Dolum × Yoğunluk / 1000 = <span className="font-mono">{plannedRef.lines[0].detail.qty_ordered} × {plannedRef.lines[0].detail.fill_qty}{plannedRef.lines[0].detail.fill_unit ? ` ${plannedRef.lines[0].detail.fill_unit}` : "ml"} × {plannedRef.lines[0].detail.density}</span> <span className="italic text-slate-400">(fire hesaba katılmaz)</span></>
              ) : "Ürün için 'Dolum Miktarı' (fill_qty) tanımlanmalı"}
            </div>
          </div>
          {/* Üretilecek Miktar (SOURCE OF TRUTH - spec rule 29-30, 78) */}
          <div className="bg-white border-2 border-indigo-400 rounded p-3">
            <div className="text-xs font-semibold text-indigo-700 mb-1 flex items-center gap-1">Üretilecek Miktar <span className="text-[9px] font-normal italic text-indigo-600">(SOURCE OF TRUTH — tüm hesapları besler)</span></div>
            <div className="flex items-center gap-2">
              <input
                type="number" step="0.001" min="0"
                value={prodKgDraft[0] ?? ""}
                onChange={e => setProdKgDraft({ ...prodKgDraft, 0: e.target.value })}
                placeholder="Ör: 105.00"
                className="flex-1 px-3 py-2 border-2 border-indigo-300 rounded font-mono text-lg font-bold text-indigo-900 focus:border-indigo-600 focus:outline-none"
                data-testid="prod-kg-input"
                disabled={order.status !== "draft" && order.auto_prod_created && (order.auto_prod_ids || []).length > 0}
              />
              <span className="text-sm font-mono text-slate-500">kg</span>
              <Btn onClick={() => applyProdKg(0)} data-testid="prod-kg-apply"><CheckCircle size={12}/> Uygula</Btn>
            </div>
            <div className="text-[10px] text-slate-500 mt-2">
              {prodKg > 0 ? <>✓ Aktif. Hammadde ihtiyacı bu kg üzerinden hesaplanıyor. Approve edildiğinde Üretim Emri {prodKg.toFixed(3)} kg ile açılır.</> : <>⚠ Değer girilmedi. Sipariş onaylanmadan önce girilmelidir.</>}
            </div>
          </div>
        </div>
      </Card>

      {/* ============= 4) ENVANTER KONTROLÜ ============= */}
      <Card className="p-4 mb-4" data-testid="section-inventory">
        <div className="text-sm font-bold mb-3 flex items-center justify-between text-slate-800">
          <div className="flex items-center gap-2"><PackageOpen size={14}/> 4. Envanter Kontrolü <span className="text-[10px] font-normal italic text-slate-500">— canlı stok · onaydan bağımsız</span></div>
          {invItems.some(it => it.status === "needs_inventory") && (
            <Btn size="sm" variant="secondary" onClick={openBulkInvSo} data-testid="so-bulk-inv-open" className="bg-fuchsia-50 border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-100">
              <Package size={12}/> Tüm Manuelleri Envantere Kaydet ({invItems.filter(it => it.status === "needs_inventory").length})
            </Btn>
          )}
        </div>
        {invItems.length === 0 && <div className="text-xs text-slate-400 italic py-3">Envanter kontrolü hesaplanamadı (Üretilecek Miktar girilmedi veya formül eksik).</div>}
        {invItems.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 text-slate-600 text-[10px] uppercase">
                <tr>
                  <th className="text-left px-2 py-2">Malzeme Kodu</th>
                  <th className="text-left px-2 py-2">Malzeme Adı</th>
                  <th className="text-left px-2 py-2">Tip</th>
                  <th className="text-left px-2 py-2">Birim</th>
                  <th className="text-right px-2 py-2">Gerekli Miktar</th>
                  <th className="text-right px-2 py-2">Mevcut Stok</th>
                  <th className="text-right px-2 py-2">Rezerve Stok</th>
                  <th className="text-right px-2 py-2">Eksik</th>
                  <th className="text-left px-2 py-2 pl-3">Durum</th>
                  <th className="text-left px-2 py-2">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {[...rawItems, ...pkgItems].map((it, i) => <InvRow key={i} it={it} onNewInv={setInvDraft} onNewPo={setPoDraft} onResPopover={setResPopover}/>)}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ============= 5) FORMÜL BİLGİLERİ ============= */}
      <Card className="p-4 mb-4" data-testid="section-formula">
        <div className="text-sm font-bold mb-3 flex items-center gap-2 text-purple-800"><Beaker size={14}/> 5. Formül Bilgileri</div>
        {!formula.id && <div className="text-xs text-slate-400 italic">Formül bulunamadı.</div>}
        {formula.id && (
          <div className="space-y-4">
            <FormulaSubsection title="Formül Listesi" testid="sub-formula" data={formula.lines || []} mById={mById} prodKg={prodKg}
              columns={[
                { label: "Malzeme Adı", render: r => (mById[r.material_id]?.name || r.material_name || "—") + (r.is_manual || !r.material_id ? " (manuel)" : "") },
                { label: "Birim", render: r => mById[r.material_id]?.unit || r.unit || "kg" },
                { label: "%", render: r => <span className="font-mono">%{fmt(r.percentage, 2)}</span> },
                { label: "Gerekli Miktar", render: r => prodKg > 0 ? <span className="font-mono font-semibold">{fmt(prodKg * (r.percentage || 0) / 100, 4)} {mById[r.material_id]?.unit || r.unit || "kg"}</span> : <span className="italic text-slate-400">Üretilecek Miktar girilmedi</span> },
                { label: "Not", render: r => r.notes || (r.is_manual || !r.material_id ? "manuel R&D" : "—") },
              ]}
            />
            <FormulaSubsection title="Ambalaj Listesi" testid="sub-packaging" data={formula.packaging_lines || []} mById={mById} qtyOrdered={qtyOrdered}
              columns={[
                { label: "Malzeme", render: r => (mById[r.material_id]?.name || r.material_name || "—") + (r.is_manual || !r.material_id ? " (manuel)" : "") },
                { label: "Birim", render: r => mById[r.material_id]?.unit || r.unit || "adet" },
                { label: "BOM Miktarı", render: r => <span className="font-mono">{fmt(r.qty_per_unit || 1, 2)}</span> },
                { label: "Toplam Gerekli", render: r => qtyOrdered > 0 ? <span className="font-mono font-semibold">{fmt(qtyOrdered * (r.qty_per_unit || 1), 0)} {mById[r.material_id]?.unit || r.unit || "adet"}</span> : "—" },
                { label: "Not", render: r => r.notes || "—" },
              ]}
            />
            <div>
              <div className="text-xs font-bold uppercase text-slate-500 mb-2">Ek Parametreler</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs" data-testid="sub-extras">
                <ExtraKv k="Proses Kaybı" v={formula.process_loss_pct != null ? `%${formula.process_loss_pct}` : "—"}/>
                <ExtraKv k="Dolum Kaybı" v={formula.fill_loss_pct != null ? `%${formula.fill_loss_pct}` : "—"}/>
                <ExtraKv k="Yoğunluk" v={formula.density != null ? formula.density : "—"}/>
                <ExtraKv k="Batch Büyüklüğü" v={formula.batch_size_kg != null ? `${formula.batch_size_kg} kg` : "—"}/>
                {(formula.extra_costs || []).map((ec, i) => <ExtraKv key={i} k={ec.name || `Extra ${i+1}`} v={`${ec.amount || 0}`}/>)}
              </div>
            </div>
            <FormulaSubsection title="QC Listesi (Referans Değerler)" testid="sub-qc" data={formula.qc_specs || []}
              columns={[
                { label: "Parametre", render: r => r.name || "—" },
                { label: "Tip", render: r => r.type || "number" },
                { label: "Alt Limit", render: r => r.lower ?? "—" },
                { label: "Hedef", render: r => r.target ?? "—" },
                { label: "Üst Limit", render: r => r.upper ?? "—" },
                { label: "Beklenen (Metin)", render: r => r.expected_text ?? "—" },
                { label: "Birim", render: r => r.unit || "—" },
              ]}
            />
            <div className="text-[10px] text-slate-400 italic border-t border-slate-100 pt-2">
              QC Referans Değerleri sadece formül üzerinden alınır. Gerçek üretim QC ölçümleri Üretim Emri sayfasında tutulur.
            </div>
          </div>
        )}
      </Card>

      {/* ---- Rezervasyon Riski Popover (Fix Pkg 03 madde 10-13) ---- */}
      <Modal open={!!resPopover} onClose={() => setResPopover(null)} title={`Rezervasyon Riski — ${resPopover?.material_name || ""}`}>
        {resPopover && (() => {
          const current = resPopover.physical_stock ?? resPopover.available ?? 0;
          const reserved = resPopover.reserved_for_other_productions || 0;
          const required = resPopover.required || 0;
          const total = reserved + required;
          const deficit = Math.max(0, total - current);
          const unit = resPopover.unit || "kg";
          const blockCreate = !!resPopover.has_active_po;
          return (
            <div className="text-sm space-y-3" data-testid="res-popover">
              <div className="text-[11px] bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded">
                <AlertTriangle size={12} className="inline mr-1"/> Temel stok yeterli ancak diğer siparişlerin rezervasyonları eklendiğinde toplam ihtiyaç mevcut stoğu aşıyor.
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-slate-50 border border-slate-200 rounded p-2">
                  <div className="text-[10px] uppercase text-slate-500">Mevcut Stok</div>
                  <div className="font-mono font-semibold text-slate-800" data-testid="res-current">{current} {unit}</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded p-2">
                  <div className="text-[10px] uppercase text-amber-700">Rezerve Stok</div>
                  <div className="font-mono font-semibold text-amber-800" data-testid="res-reserved">{reserved} {unit}</div>
                </div>
                <div className="bg-indigo-50 border border-indigo-200 rounded p-2">
                  <div className="text-[10px] uppercase text-indigo-700">Bu Sipariş İçin Gerekli</div>
                  <div className="font-mono font-semibold text-indigo-800" data-testid="res-required">{required} {unit}</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded p-2">
                  <div className="text-[10px] uppercase text-slate-500">Toplam İhtiyaç</div>
                  <div className="font-mono font-semibold text-slate-800" data-testid="res-total">{total} {unit}</div>
                </div>
                {deficit > 0 && (
                  <div className="col-span-2 bg-red-50 border border-red-300 rounded p-2">
                    <div className="text-[10px] uppercase text-red-700">Eksik Miktar</div>
                    <div className="font-mono font-bold text-red-800" data-testid="res-deficit">{deficit} {unit}</div>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <Btn variant="secondary" onClick={() => setResPopover(null)}>Kapat</Btn>
                {deficit > 0 && !blockCreate && (
                  <Btn onClick={() => {
                    setPoDraft({
                      material_id: resPopover.material_id,
                      material_name: resPopover.material_name,
                      unit,
                      supplier: "",
                      qty: deficit,
                      unit_price: 0,
                      expected_delivery: "",
                      requirement_reason: "RESERVATION_RISK",
                    });
                    setResPopover(null);
                  }} data-testid="res-create-po"><ShoppingCart size={12}/> Sipariş Oluştur ({deficit} {unit})</Btn>
                )}
                {deficit > 0 && blockCreate && (
                  <div className="text-[11px] bg-indigo-50 border border-indigo-300 text-indigo-800 px-3 py-1.5 rounded self-center" data-testid="res-blocked">
                    Bu SO+malzeme için aktif satın alma zaten var.
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* ---- Manuel malzeme → envanter modal ---- */}
      <Modal open={!!invDraft} onClose={() => setInvDraft(null)} title={`Yeni Envanter Kaydı — ${invDraft?.material_name || ""}`}>
        <div className="text-xs bg-fuchsia-50 border border-fuchsia-200 rounded p-2 mb-3 text-fuchsia-800">
          <Package size={12} className="inline mr-1"/> Ar-Ge / Formülden gelen manuel malzeme için envanter kaydı oluşturulacak. Kaydedince ilgili formül satırları otomatik bağlanır.
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input label="Malzeme Adı *" value={invDraft?.material_name || ""} onChange={e => setInvDraft({ ...invDraft, material_name: e.target.value })} data-testid="inv-draft-name"/>
          <Select label="Kategori *" value={invDraft?.category || ""} onChange={e => setInvDraft({ ...invDraft, category: e.target.value })} data-testid="inv-draft-category">
            <option value="raw_material">Hammadde</option><option value="active">Aktif</option>
            <option value="fragrance">Koku</option><option value="aux">Yardımcı</option>
            <option value="bulk">Bulk</option><option value="bottle">Şişe</option>
            <option value="jar">Kavanoz</option><option value="cap">Kapak</option>
            <option value="pump">Pompa</option><option value="dropper">Damlalık</option>
            <option value="label">Etiket</option><option value="product_carton">Ürün Kutusu</option>
            <option value="shipping_carton">Sevkiyat Kolisi</option><option value="other_packaging">Diğer Ambalaj</option>
          </Select>
          <Select label="Birim" value={invDraft?.unit || "kg"} onChange={e => setInvDraft({ ...invDraft, unit: e.target.value })}>
            <option value="kg">kg</option><option value="g">g</option><option value="l">l</option><option value="ml">ml</option>
            <option value="adet">adet</option><option value="box">kutu</option><option value="carton">koli</option>
          </Select>
          <Input label="Birim Maliyet" type="number" step="0.0001" value={invDraft?.unit_cost || ""} onChange={e => setInvDraft({ ...invDraft, unit_cost: e.target.value })}/>
          <Select label="Para Birimi" value={invDraft?.currency || "EUR"} onChange={e => setInvDraft({ ...invDraft, currency: e.target.value })}>
            <option value="EUR">EUR</option><option value="USD">USD</option><option value="TRY">TRY</option>
          </Select>
          <Input label="Tercih Edilen Tedarikçi" value={invDraft?.primary_supplier || ""} onChange={e => setInvDraft({ ...invDraft, primary_supplier: e.target.value })}/>
          <Input label="Kritik Stok" type="number" step="0.01" value={invDraft?.critical_stock || ""} onChange={e => setInvDraft({ ...invDraft, critical_stock: e.target.value })}/>
          <Input label="Tedarik Süresi (gün)" type="number" value={invDraft?.lead_time_days || ""} onChange={e => setInvDraft({ ...invDraft, lead_time_days: e.target.value })}/>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Btn variant="secondary" onClick={() => setInvDraft(null)}>Vazgeç</Btn>
          <Btn onClick={saveInv} disabled={!!invBusyKey} data-testid="inv-draft-save">
            <Package size={14}/> {invBusyKey ? "Kaydediliyor..." : "Kaydet & Bağla"}
          </Btn>
        </div>
      </Modal>

      {/* ---- PO taslak modal ---- */}
      <Modal open={!!poDraft} onClose={() => setPoDraft(null)} title={`Satın Alma Taslağı — ${poDraft?.material_name || ""}`}>
        <div className="text-xs bg-indigo-50 border border-indigo-200 rounded p-2 mb-3 text-indigo-800">
          <ShoppingCart size={12} className="inline mr-1"/> Aynı tedarikçiye ait mevcut taslak PO varsa otomatik konsolide edilir.
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Select label="Tedarikçi *" value={poDraft?.supplier || ""} onChange={e => setPoDraft({ ...poDraft, supplier: e.target.value })} data-testid="so-po-supplier">
            <option value="">— Kayıtlı tedarikçilerden seçin —</option>
            {suppliers.filter(s => s.active !== false).map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
          </Select>
          <Input label="Beklenen Teslim" type="date" value={poDraft?.expected_delivery || ""} onChange={e => setPoDraft({ ...poDraft, expected_delivery: e.target.value })}/>
          <Input label={`Miktar (${poDraft?.unit || ""})`} type="number" step="0.01" value={poDraft?.qty || ""} onChange={e => setPoDraft({ ...poDraft, qty: e.target.value })} data-testid="so-po-qty"/>
          <Input label="Birim Fiyat" type="number" step="0.01" value={poDraft?.unit_price || ""} onChange={e => setPoDraft({ ...poDraft, unit_price: e.target.value })}/>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Btn variant="secondary" onClick={() => setPoDraft(null)}>Vazgeç</Btn>
          <Btn onClick={savePo} data-testid="so-po-save"><ShoppingCart size={14}/> Sipariş Oluştur</Btn>
        </div>
      </Modal>

      {/* ---- Toplu Envantere Kaydet Wizard (Iter 92) ---- */}
      <BulkInvWizard bulkInv={bulkInv} setBulkInv={setBulkInv} bulkBusy={bulkBusy} onSave={saveBulkInvSo} />
    </div>
  );
}

/* ---------- helpers ---------- */
function Info({ label, value, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase font-semibold text-slate-500 mb-0.5">{label}</div>
      <div className="text-sm" data-testid={testid}><span>{value}</span></div>
    </div>
  );
}
function ExtraKv({ k, v }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded p-2">
      <div className="text-[10px] uppercase text-slate-500">{k}</div>
      <div className="font-mono font-semibold text-slate-700">{v}</div>
    </div>
  );
}
function FormulaSubsection({ title, data, columns, testid }) {
  return (
    <div>
      <div className="text-xs font-bold uppercase text-slate-500 mb-2">{title}</div>
      {(!data || data.length === 0) ? <div className="text-xs italic text-slate-400" data-testid={`${testid}-empty`}>Kayıt yok</div> :
        <div className="overflow-x-auto" data-testid={testid}>
          <table className="w-full text-xs">
            <thead className="bg-slate-100 text-slate-600 text-[10px] uppercase">
              <tr>{columns.map((c, i) => <th key={i} className="text-left px-2 py-2">{c.label}</th>)}</tr>
            </thead>
            <tbody>
              {data.map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  {columns.map((c, j) => <td key={j} className="px-2 py-1.5">{c.render ? c.render(r) : r[c.key]}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }
    </div>
  );
}
function InvRow({ it, onNewInv, onNewPo, onResPopover }) {
  // primary status is exactly per spec rules 41-45, purchase button per rule 47
  const primary = it.status; // sufficient | in_qc | po_opened | shortage | needs_inventory | material_error
  const meta = {
    sufficient:      { label: "Yeterli",           cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    in_qc:           { label: "QC / Karantinada",  cls: "bg-amber-100 text-amber-800 border-amber-300" },
    po_opened:       { label: "Satın Alma Açıldı", cls: "bg-indigo-100 text-indigo-800 border-indigo-300" },
    shortage:        { label: "Eksik",             cls: "bg-red-100 text-red-800 border-red-300" },
    needs_inventory: { label: "Envanter Kaydı Gerekli", cls: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300" },
    material_error:  { label: "Malzeme Hatası",    cls: "bg-red-200 text-red-900 border-red-400" },
  }[primary] || { label: primary, cls: "bg-slate-100 text-slate-700 border-slate-300" };
  // "Eksik" kolonu: sadece net eksik miktar (Fix Pkg 03 madde 6). Rezervasyon riski
  // ayrı bir "!" ile Rezerve Stok kolonunda gösterilir; buradaki değer sadece stok
  // yetersizse (shortage / needs_inventory) doldurulur.
  const netShortage = primary === "shortage" ? it.shortage
                    : primary === "needs_inventory" ? it.required : 0;
  const blockCreate = !!it.has_active_po;
  // Kullanıcı isteği (Iter 93): "Rezerve" kolonu artık TÜM rezervasyonları göster
  // (bu SO'nun kendi rezervasyonu + başka SO'ların rezervasyonları). Rezervasyon riski
  // hesabı (need + reserved_elsewhere > physical) yine önceki gibi çalışıyor.
  const reservedQty = it.reserved_total || 0;
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50">
      <td className="px-2 py-2 font-mono text-[11px]">{it.material_id ? ("HM..." + String(it.material_id).slice(-5)) : "—"}</td>
      <td className="px-2 py-2 font-medium">{it.material_name || "—"}{it.needs_inventory && <span className="ml-1 text-[10px] italic text-fuchsia-700">(manuel R&D)</span>}</td>
      <td className="px-2 py-2">{it.kind === "raw" ? "Hammadde" : "Ambalaj"}</td>
      <td className="px-2 py-2">{it.unit || "kg"}</td>
      <td className="px-2 py-2 text-right font-mono">{it.required} {it.unit}</td>
      <td className="px-2 py-2 text-right font-mono">{it.needs_inventory ? <span className="text-slate-400">—</span> : `${it.physical_stock ?? it.available} ${it.unit}`}</td>
      <td className="px-2 py-2 text-right font-mono">
        {reservedQty > 0
          ? (
            it.reservation_warning
              ? (
                <button
                  type="button"
                  onClick={() => onResPopover(it)}
                  className="inline-flex items-center gap-1 text-red-700 hover:bg-red-50 border border-red-300 rounded px-1.5 py-0.5"
                  title="Rezervasyon riski var — detay için tıklayın"
                  data-testid={`res-warn-${it.material_id || it.material_name}`}
                >
                  <span>{reservedQty} {it.unit}</span>
                  <AlertTriangle size={12} className="text-red-600" />
                </button>
              )
              : <span className="text-amber-700" title={`Bu SO: ${it.own_reservations || 0} · Diğer: ${it.reserved_for_other_productions || 0}`}>{reservedQty} {it.unit}</span>
          )
          : <span className="text-slate-300">—</span>}
      </td>
      <td className="px-2 py-2 text-right font-mono">{netShortage > 0 ? <span className="text-red-700 font-semibold">{netShortage} {it.unit}</span> : <span className="text-slate-300">—</span>}</td>
      <td className="px-2 py-2 pl-3">
        <span className={`inline-block text-[11px] font-semibold border rounded px-2 py-0.5 ${meta.cls}`} data-testid={`inv-status-${it.material_id || it.material_name}`}>{meta.label}</span>
      </td>
      <td className="px-2 py-2">
        <div className="flex flex-col gap-1">
          {primary === "needs_inventory" && (
            <button onClick={() => onNewInv({
              material_name: it.manual_material_name || it.material_name,
              unit: it.unit || "kg",
              category: it.kind === "packaging" ? "other_packaging" : "raw_material",
              unit_cost: 0, primary_supplier: "", currency: "EUR",
              min_stock: 0, critical_stock: 0, lead_time_days: 0,
              suggested_qty: it.required,
            })} className="text-[11px] bg-fuchsia-600 hover:bg-fuchsia-700 text-white px-2 py-1 rounded flex items-center gap-1 w-max" data-testid={`new-inventory-${it.material_name}`}>
              <Package size={11}/> Yeni Envanter Kaydı
            </button>
          )}
          {primary === "shortage" && !blockCreate && (
            <button onClick={() => onNewPo({
              material_id: it.material_id, material_name: it.material_name, unit: it.unit || "kg",
              supplier: "", qty: it.shortage,
              unit_price: 0, expected_delivery: "",
              requirement_reason: "PHYSICAL_SHORTAGE",
            })} className="text-[11px] text-white px-2 py-1 rounded flex items-center gap-1 w-max bg-emerald-600 hover:bg-emerald-700" data-testid={`po-draft-shortage-${it.material_id || it.material_name}`}>
              <ShoppingCart size={11}/> Sipariş Oluştur
            </button>
          )}
          {blockCreate && (primary === "shortage" || (primary === "sufficient" && it.reservation_warning)) && (
            <div className="text-[10px] bg-indigo-50 border border-indigo-300 text-indigo-800 px-2 py-1 rounded w-max" data-testid={`po-blocked-${it.material_id || it.material_name}`}>
              <ShoppingCart size={10} className="inline mr-1"/> Satın Alma Açıldı
            </div>
          )}
          {it.pending_po_ref && <div className="text-[10px] bg-slate-100 border border-slate-300 text-slate-700 px-2 py-1 rounded w-max">Satın Alma Onay Bekliyor — {it.pending_po_ref.po_no}</div>}
          {primary === "po_opened" && (it.approved_po_refs || []).map((p, k) => <div key={k} className="text-[10px] text-indigo-800"><span className="font-semibold">{p.po_no}</span> · {p.qty} {it.unit}{p.expected_delivery && <> · <span className="font-mono">{(p.expected_delivery || "").slice(0, 10)}</span></>}</div>)}
          {primary === "in_qc" && (it.qc_lot_refs || []).map((q, k) => <div key={k} className="text-[10px] text-amber-800"><span className="font-mono">{q.lot_code}</span> · {q.qty} {it.unit} · {q.status === "quarantine" ? "Karantina" : "QC"}</div>)}
        </div>
      </td>
    </tr>
  );
}
